import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import BoltRoundedIcon from '@mui/icons-material/BoltRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded'
import RadioButtonCheckedRoundedIcon from '@mui/icons-material/RadioButtonCheckedRounded'
import StorageRoundedIcon from '@mui/icons-material/StorageRounded'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import { AI_AGENT_COSTS, AI_SURFACE_LABELS, generatePulseSummary, getWorkspacePulse, rephraseWorkspaceDigest, scanWorkspaceBudgetAdvisor, scanWorkspaceRisk, subscribeWorkspacePulseEvents, type WorkspacePulseEvent, type WorkspacePulseSnapshot } from '../../services/aiAgentBridge'
import { useToast } from '../../context/useToast'
import { formatCredits as formatCreditsShared } from '../Credits/formatCredits'
import { useAuth } from '../../context/useAuth'
import { useTimeline } from '../../context/useTimeline'
import { firebaseAuth } from '../../services/firebase'
import { isNoSignInPreviewActive } from '../../debug'
import { buildLocalWorkspacePulseSnapshot } from '../../services/ai/context/localWorkspacePulse'
import { AttachmentIntelligencePanel } from './AttachmentIntelligencePanel'
import type { AttachmentPlanningDigestSnapshot } from '../../services/ai/attachment/attachmentPlanningIntelligence'
import { RailChipTabs, StatusChip } from '../Common'
import { Button } from '../Common/Button'
import { IconButton } from '../Common/IconButton'
import { Pressable } from '../Common/Pressable'

interface WorkspacePulseProps {
  workspaceId: string
  presentation?: 'popover' | 'panel'
  premiumPanel?: WorkspacePulsePremiumPanel | null
}

export interface WorkspacePulsePremiumPanel {
  digest: AttachmentPlanningDigestSnapshot | null
  status: 'idle' | 'analyzing' | 'ready' | 'error'
  error?: string | null
  digestFingerprint?: string | null
  sourceCount: number
}

type WorkspacePulseCarouselSection = {
  id: string
  label: string
  content: ReactNode
}

const statusCopy = {
  HEALTHY: 'Stable',
  WATCH: 'Watch',
  CRITICAL: 'Critical',
} as const

const pulseErrorMessage = (error: unknown, fallback: string) => {
  const code = typeof error === 'object' && error !== null && 'code' in error ? String((error as { code?: unknown }).code) : ''
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : fallback
  if (/unauthenticated/i.test(`${code} ${message}`)) {
    return 'Sign in again to use Workspace Pulse. Firebase has not restored a secure auth token for this session.'
  }
  if (/resource-exhausted/i.test(`${code} ${message}`)) {
    return message || 'Workspace Pulse summary needs more credits.'
  }
  return message || fallback
}

export const WorkspacePulse = ({ workspaceId, presentation = 'popover', premiumPanel = null }: WorkspacePulseProps) => {
  const { showToast } = useToast()
  const { user, isAuthLoading } = useAuth()
  const { elements } = useTimeline()
  const localPulse = useMemo(() => buildLocalWorkspacePulseSnapshot(workspaceId, elements), [elements, workspaceId])
  const [pulse, setPulse] = useState<WorkspacePulseSnapshot | null>(null)
  const [summary, setSummary] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [insightAction, setInsightAction] = useState<'RISK' | 'BUDGET' | 'DIGEST' | null>(null)
  const [insightText, setInsightText] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [pulseEvents, setPulseEvents] = useState<WorkspacePulseEvent[]>([])
  const [lastViewedPremiumFingerprint, setLastViewedPremiumFingerprint] = useState<string | null>(null)
  const [activeCarouselIndex, setActiveCarouselIndex] = useState(0)
  const isPanel = presentation === 'panel'
  const hasPremiumSection = Boolean(
    premiumPanel
    && (
      premiumPanel.sourceCount > 0
      || premiumPanel.digest
      || premiumPanel.status !== 'idle'
      || premiumPanel.error
    ),
  )
  const premiumNotificationFingerprint = premiumPanel?.digestFingerprint
    ?? (premiumPanel?.status === 'analyzing' ? `analyzing:${premiumPanel.sourceCount}` : null)
  const hasPremiumNotification = Boolean(
    !isPanel
    && hasPremiumSection
    && premiumNotificationFingerprint
    && premiumNotificationFingerprint !== lastViewedPremiumFingerprint,
  )
  const toggleMetric = hasPremiumSection
    ? premiumPanel?.sourceCount ?? 0
    : loading
      ? '...'
      : pulse?.riskScore ?? 0
  const liveFeedItems = (pulseEvents.length
    ? pulseEvents
    : [{
        id: 'none',
        title: 'No live alerts.',
        summary: 'Meeting readiness and agent suggestions will appear here.',
        severity: 'INFO',
      }]).slice(0, 3)
  const planItems = (pulse?.topRisks.length
    ? pulse.topRisks
    : [{ id: 'none', summary: 'No materialized risks are currently open.', source: 'CLEAR' }]).slice(0, 3)
  const browserItems = (pulse?.activity.latest.length
    ? pulse.activity.latest
    : [{ id: 'none', action: 'IDLE', summary: 'No recent workspace activity.' }]).slice(0, 3)
  const sourceRows = [
    { id: 'budget', label: 'Budget used', value: `${Math.round((pulse?.budget.utilization ?? 0) * 100)}%` },
    { id: 'credits', label: 'Credits', value: formatCreditsShared(pulse?.walletCredits ?? 0, { suffix: 'none' }) },
  ]
  const carouselSections: WorkspacePulseCarouselSection[] = [
    {
      id: 'live-feed',
      label: 'Live feed',
      content: (
        <div className="workspacePulseSlideSignals">
          {liveFeedItems.map((event) => (
            <div className={`workspacePulseSignal workspacePulseSignal--${String(event.severity).toLowerCase()}`} key={event.id}>
              <RadioButtonCheckedRoundedIcon fontSize="small" />
              <strong>{event.title}</strong>
              <span>{event.summary}</span>
            </div>
          ))}
        </div>
      ),
    },
    {
      id: 'risk-scanner',
      label: AI_SURFACE_LABELS.RISK_SCANNER.label,
      content: (
        <div className="workspacePulseSlideSignals">
          {planItems.map((risk) => (
            <div className="workspacePulseSignal" key={risk.id}>
              <InsightsRoundedIcon fontSize="small" />
              <strong>{risk.source}</strong>
              <span>{risk.summary}</span>
            </div>
          ))}
        </div>
      ),
    },
    {
      id: 'browser',
      label: 'Browser',
      content: (
        <div className="workspacePulseSlideSignals">
          {browserItems.map((activity) => (
            <div className="workspacePulseSignal" key={activity.id}>
              <AutoAwesomeRoundedIcon fontSize="small" />
              <strong>{activity.action}</strong>
              <span>{activity.summary}</span>
            </div>
          ))}
        </div>
      ),
    },
    {
      id: 'sources',
      label: 'Sources',
      content: (
        <div className="workspacePulseSlideRows">
          {sourceRows.map((row) => (
            <div className="workspacePulseRow workspacePulseRow--compact" key={row.id}>
              <span>{row.label}</span>
              <strong>{row.value}</strong>
            </div>
          ))}
        </div>
      ),
    },
  ]
  if (hasPremiumSection && premiumPanel) {
    carouselSections.push({
      id: 'premium-signals',
      label: 'Attachment intelligence',
      content: (
        <AttachmentIntelligencePanel
          digest={premiumPanel.digest}
          status={premiumPanel.status}
          error={premiumPanel.error}
          sourceCount={premiumPanel.sourceCount}
          compact
        />
      ),
    })
  }

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    if (isAuthLoading) {
      return () => {
        cancelled = true
      }
    }
    if (!isNoSignInPreviewActive() && !user?.uid) {
      setPulse(localPulse)
      setError(null)
      setLoading(false)
      return () => {
        cancelled = true
      }
    }
    void getWorkspacePulse(workspaceId)
      .then((snapshot) => {
        if (!cancelled) setPulse(snapshot)
      })
      .catch((caught) => {
        if (!cancelled) {
          console.warn('[workspace-pulse] using local pulse fallback', caught)
          setPulse(localPulse)
          setError(null)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isAuthLoading, localPulse, user?.uid, workspaceId])

  useEffect(() => {
    if (isAuthLoading || (!isNoSignInPreviewActive() && !user?.uid)) {
      setPulseEvents([])
      return undefined
    }
    return subscribeWorkspacePulseEvents(workspaceId, setPulseEvents, (caught) => {
      console.warn('[workspace-pulse] event subscription failed', caught)
      setPulseEvents([])
    })
  }, [isAuthLoading, user?.uid, workspaceId])

  useEffect(() => {
    if ((isOpen || isPanel) && premiumNotificationFingerprint) {
      setLastViewedPremiumFingerprint(premiumNotificationFingerprint)
    }
  }, [isOpen, isPanel, premiumNotificationFingerprint])

  useEffect(() => {
    setActiveCarouselIndex((current) => Math.min(current, Math.max(carouselSections.length - 1, 0)))
  }, [carouselSections.length])

  const toneColor = useMemo(() => {
    if (pulse?.healthState === 'CRITICAL') return 'var(--danger)'
    if (pulse?.healthState === 'WATCH') return 'var(--warning)'
    return 'var(--success)'
  }, [pulse?.healthState])

  const handleNarrative = async () => {
    setSummaryLoading(true)
    try {
      if (!isNoSignInPreviewActive() && !firebaseAuth.currentUser) {
        throw new Error('Sign in again to use Workspace Pulse. Firebase has not restored a secure auth token for this session.')
      }
      const result = await generatePulseSummary(workspaceId)
      setSummary(result.summary)
      showToast(result.cached ? 'Loaded cached Pulse summary.' : `Pulse summary generated for ${result.creditsCharged} credits.`, 'success')
    } catch (caught) {
      showToast(pulseErrorMessage(caught, 'Could not generate Pulse summary.'), 'error')
    } finally {
      setSummaryLoading(false)
    }
  }

  const handleRiskScan = async () => {
    setInsightAction('RISK')
    try {
      const result = await scanWorkspaceRisk(workspaceId)
      const topLines = result.topRisks.slice(0, 5).map((risk) => `• ${risk.summary}`).join('\n')
      const header = `Risk score ${result.riskScore} · ${result.openRiskCount} open · ${result.failedAutomationCount} failed automations · ${result.stockShortageCount} stock shortages.`
      setInsightText(topLines ? `${header}\n${topLines}` : header)
      showToast(`Risk scan ran for ${result.creditsCharged} credits.`, 'success')
    } catch (caught) {
      showToast(pulseErrorMessage(caught, 'Could not run risk scan.'), 'error')
    } finally {
      setInsightAction(null)
    }
  }

  const handleBudgetAdvisor = async () => {
    setInsightAction('BUDGET')
    try {
      const result = await scanWorkspaceBudgetAdvisor(workspaceId)
      const advisories = result.advisories.slice(0, 5).map((line) => `• ${line}`).join('\n')
      const header = `Budget utilization ${Math.round(result.budgetUtilization * 100)}% · ${result.overBudgetCount} categories over budget · ${result.stockShortageCount} stock shortages.`
      setInsightText(advisories ? `${header}\n${advisories}` : header)
      showToast(`Budget advisor ran for ${result.creditsCharged} credits.`, 'success')
    } catch (caught) {
      showToast(pulseErrorMessage(caught, 'Could not run Budget Advisor.'), 'error')
    } finally {
      setInsightAction(null)
    }
  }

  const handleDigestRephrase = async () => {
    setInsightAction('DIGEST')
    try {
      const events = (pulse?.activity?.latest ?? []).slice(0, 12).map((event) => ({
        action: event.action,
        summary: event.summary,
        targetTitle: event.summary,
      }))
      const result = await rephraseWorkspaceDigest({
        workspaceId,
        digestHeadline: 'Workspace digest preview',
        events,
      })
      setInsightText(result.rephrased || 'No digest content.')
      showToast(`Digest rephrased for ${result.creditsCharged} credits.`, 'success')
    } catch (caught) {
      showToast(pulseErrorMessage(caught, 'Could not rephrase the digest.'), 'error')
    } finally {
      setInsightAction(null)
    }
  }

  return (
    <div className={`workspacePulseDock${isPanel ? ' isPanel' : ''}`} style={{ '--workspace-pulse-tone': toneColor } as CSSProperties}>
      {!isPanel && (
        <Pressable
          className={`workspacePulseToggle${isOpen ? ' isOpen' : ''}${hasPremiumNotification ? ' hasNotification' : ''}`}
          onClick={() => setIsOpen((value) => !value)}
          aria-expanded={isOpen}
          aria-controls="workspace-pulse-panel"
        >
          <RadioButtonCheckedRoundedIcon fontSize="small" />
          <span>Pulse</span>
          {hasPremiumNotification && <StatusChip tone="info" size="sm">New</StatusChip>}
          <strong>{toggleMetric}</strong>
        </Pressable>
      )}

      <AnimatePresence>
        {(isOpen || isPanel) && (
          <motion.section
            id="workspace-pulse-panel"
            className={`workspacePulse${isPanel ? ' isPanel' : ''} workspacePulse--${pulse?.healthState ?? 'loading'}`}
            aria-label="Workspace Pulse"
            initial={isPanel ? false : { opacity: 0, x: 18, scale: 0.98 }}
            animate={isPanel ? { opacity: 1 } : { opacity: 1, x: 0, scale: 1 }}
            exit={isPanel ? { opacity: 0 } : { opacity: 0, x: 18, scale: 0.98 }}
            transition={{ duration: 0.16, ease: 'easeOut' }}
          >
            <div className="workspacePulseBody">
              <div className="workspacePulseHead">
                <div>
                  <p className="workspacePulseEyebrow">Environment</p>
                  <h2>Workspace Pulse</h2>
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => void handleNarrative()}
                  disabled={!pulse || summaryLoading}
                >
                  {summaryLoading ? 'Generating' : `AI Summary (${AI_AGENT_COSTS.PULSE_SUMMARY} Cr)`}
                </Button>
              </div>

              <div className="workspacePulseInsightRow">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => void handleRiskScan()}
                  disabled={!pulse || insightAction !== null}
                  aria-label="Run AI risk scanner"
                >
                  {insightAction === 'RISK' ? 'Scanning…' : `Scan Risks (${AI_AGENT_COSTS.RISK_SCANNER} Cr)`}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => void handleBudgetAdvisor()}
                  disabled={!pulse || insightAction !== null}
                  aria-label="Run AI budget advisor"
                >
                  {insightAction === 'BUDGET' ? 'Advising…' : `Budget Advice (${AI_AGENT_COSTS.BUDGET_ADVISOR} Cr)`}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => void handleDigestRephrase()}
                  disabled={!pulse || insightAction !== null || (pulse?.activity?.latest?.length ?? 0) === 0}
                  aria-label="Rephrase workspace digest"
                >
                  {insightAction === 'DIGEST' ? 'Rephrasing…' : `Rephrase Digest (${AI_AGENT_COSTS.DIGEST_REPHRASE} Cr)`}
                </Button>
              </div>

              {insightText && (
                <div className="workspacePulseInsightOutput" role="status">
                  <p>{insightText}</p>
                </div>
              )}

              {error ? (
                <p className="workspacePulseError">{error}</p>
              ) : (
                <>
                  <div className="workspacePulseRows">
                    <div className="workspacePulseRow">
                      <RadioButtonCheckedRoundedIcon fontSize="small" />
                      <span>{pulse ? statusCopy[pulse.healthState] : 'Loading'}</span>
                      <strong>{loading ? '...' : pulse?.riskScore ?? 0}</strong>
                    </div>
                    <div className="workspacePulseRow">
                      <ErrorOutlineRoundedIcon fontSize="small" />
                      <span>Open risks</span>
                      <strong>{pulse?.openRiskCount ?? 0}</strong>
                    </div>
                    <div className="workspacePulseRow">
                      <BoltRoundedIcon fontSize="small" />
                      <span>Automation failures</span>
                      <strong>{pulse?.failedAutomationCount ?? 0}</strong>
                    </div>
                    <div className="workspacePulseRow">
                      <StorageRoundedIcon fontSize="small" />
                      <span>Resource pressure</span>
                      <strong>{pulse?.stockShortageCount ?? 0}</strong>
                    </div>
                  </div>

                  <div className="workspacePulseCarousel">
                    <div className="workspacePulseCarouselHeader">
                      <RailChipTabs
                        orientation="horizontal"
                        ariaLabel="Pulse sections"
                        items={carouselSections.map((section, index) => ({
                          key: String(index),
                          label: section.label,
                        }))}
                        value={String(activeCarouselIndex)}
                        onChange={(key) => setActiveCarouselIndex(Number(key))}
                      />
                      {carouselSections.length > 1 && (
                        <div className="workspacePulseCarouselNav">
                          <IconButton
                            size="sm"
                            variant="tertiary"
                            onClick={() => setActiveCarouselIndex((current) => (current - 1 + carouselSections.length) % carouselSections.length)}
                            label="Previous Pulse section"
                            icon={<ChevronLeftRoundedIcon fontSize="small" />}
                          />
                          <IconButton
                            size="sm"
                            variant="tertiary"
                            onClick={() => setActiveCarouselIndex((current) => (current + 1) % carouselSections.length)}
                            label="Next Pulse section"
                            icon={<ChevronRightRoundedIcon fontSize="small" />}
                          />
                        </div>
                      )}
                    </div>

                    <div className="workspacePulseCarouselViewport">
                      <div
                        className="workspacePulseCarouselTrack"
                        style={{ transform: `translateX(-${activeCarouselIndex * 100}%)` }}
                      >
                        {carouselSections.map((section) => (
                          <section className="workspacePulseCarouselSlide" key={section.id}>
                            <p className="workspacePulseLabel">{section.label}</p>
                            {section.content}
                          </section>
                        ))}
                      </div>
                    </div>
                  </div>

                  {summary && (
                    <motion.p className="workspacePulseSummary" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}>
                      {summary}
                    </motion.p>
                  )}
                </>
              )}
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  )
}
